import React, { useEffect, useState, useRef } from 'react';
import { datasetID, boundaries } from '../../collection/store/collection.selectors';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import { CircularProgress } from '@mui/material';
import { setDriveVideoURL } from '../store/drive.actions';
import { selectAllDrives } from '../store/drive.selectors';
import { loadGoogleMaps } from '../../../utils/common/googleMapsLoader';

const MasterMapComponent = (props) => {
  const dispatch = useDispatch();
  const { setSelectedDrive, isLoading } = props;
  const datasetIDValue = useSelector(datasetID);
  const datasetBoundaries = useSelector(boundaries);
  const drives = useSelector(selectAllDrives);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoveredDriveId, setHoveredDriveId] = useState(null);

  const infoWindowRef = useRef(null);
  const mapRef = useRef(null);

  // Suppress ResizeObserver errors from map libraries
  useEffect(() => {
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
      }
    };

    // eslint-disable-next-line no-console
    const originalError = console.error;
    // eslint-disable-next-line no-console
    console.error = (...args) => {
      const message = args?.[0]?.toString?.() || '';
      if (message.includes('ResizeObserver loop')) {
        return;
      }
      originalError.apply(console, args);
    };

    window.addEventListener('error', handleError);

    return () => {
      window.removeEventListener('error', handleError);
      // eslint-disable-next-line no-console
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    loadGoogleMaps('AIzaSyBrYTn6FUEGr_84F1UPtsYa7l_sp4bWWMo')
      .then(() => setMapLoaded(true))
      .catch(() => {});
  }, []);

  // Initialize map
  useEffect(() => {
    if (!mapLoaded) return;

    const initMap = () => {
      const mapDiv = document.getElementById('drive-map');
      if (!mapDiv) {
        return;
      }

      mapRef.current = new window.google.maps.Map(mapDiv, {
        center: { lat: 39.8283, lng: -98.5795 },
        zoom: 4,
        mapId: '99b311845891871b',
      });

      infoWindowRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: true,
      });
    };
    initMap();
  }, [mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !datasetBoundaries) return;

    const { north, south, east, west } = datasetBoundaries;
    if (north && south && east && west) {
      const bounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(south, west),
        new window.google.maps.LatLng(north, east)
      );
      mapRef.current.fitBounds(bounds, 50);
    }
  }, [mapLoaded, datasetBoundaries]);

  useEffect(() => {
    if (!datasetIDValue || !mapLoaded || !mapRef.current) return;

    const datasetLayer = mapRef.current.getDatasetFeatureLayer(datasetIDValue);
    if (!datasetLayer) return;

    datasetLayer.style = (options) => {
      const driveID = options.feature.datasetAttributes['driveID'];
      const isHovered = driveID === hoveredDriveId;

      return {
        strokeColor: isHovered ? '#00E5FF' : '#6200EE',
        strokeWeight: isHovered ? 6 : 3,
        strokeOpacity: isHovered ? 1.0 : 0.7,
      };
    };

    const clickListener = datasetLayer.addListener('click', (event) => {
      const feature = event.features[0];
      const driveID = feature.datasetAttributes['driveID'];
      const driveObject = drives?.find((drive) => drive.id === driveID);
      if (driveObject) {
        if (setSelectedDrive) {
          setSelectedDrive(driveObject);
        }
        localStorage.setItem('selectedDriveRow', JSON.stringify(driveObject));
        dispatch(setDriveVideoURL(driveObject.video_url || null));
      }
    });

    const mouseMoveListener = datasetLayer.addListener('mousemove', (event) => {
      const feature = event.features[0];
      const driveID = feature.datasetAttributes['driveID'];
      const driveName = feature.datasetAttributes['driveName'];

      if (driveID !== hoveredDriveId) {
        setHoveredDriveId(driveID);

        const contentString = `
          <div style="font-family: Inter, Roboto, Arial, sans-serif; padding-top: 10px;">
              <style>
                    .gm-style-iw-chr {
                      display: none !important;
                    }
                    .gm-style-iw button[aria-label="Close"] {
                      display: none !important;
                    }
                    .gm-ui-hover-effect {
                      display: none !important;
                    }
              </style>
              <div style="font-weight:600; font-size:12px; color:#0f172a;">Drive Name</div>
              <div style="font-size:14px; font-weight:700; margin-bottom:8px; color:#0b61d6;">
              ${driveName}
              </div>
            </div>
        `;

        infoWindowRef.current.setContent(contentString);
        infoWindowRef.current.setPosition(event.latLng);

        infoWindowRef.current.setOptions({
          pixelOffset: new window.google.maps.Size(0, -5),
        });

        infoWindowRef.current.open(mapRef.current);
      }
    });

    const mapMoveListener = mapRef.current.addListener('mousemove', (event) => {
      if (hoveredDriveId !== null) {
        setHoveredDriveId(null);
        infoWindowRef.current.close();
      }
    });

    return () => {
      mouseMoveListener.remove();
      mapMoveListener.remove();
      clickListener.remove();
    };
  }, [datasetIDValue, mapLoaded, hoveredDriveId]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        py: 2,
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        id="drive-map"
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      />
      {(!mapLoaded || isLoading) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.65)',
            zIndex: 120,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

MasterMapComponent.propTypes = {
  setSelectedDrive: PropTypes.func,
  isLoading: PropTypes.bool,
};
MasterMapComponent.defaultProps = {
  isLoading: false,
};
export default MasterMapComponent;
